import { FilterCapabilityConfig, FilterFieldCapability } from '@omnea-app/packages/backend/filtering/types/baseFilterTypes'
import { getSupportedOperators } from '@omnea-app/packages/backend/filtering/types/supportedOperators'
import { REQUESTS_FILTER_CAPABILITIES } from './requestsFilterCapabilities'

// Isomorphic: the single arbiter of which request filter/sort clauses /paginated-v2 can serve. The FE reads
// it to keep unservable clauses out of selectable saved views and restored grid state; the backend reads the
// same predicates to 400 an unsupported clause instead of silently dropping it and over-returning. Must stay
// FE-safe (string data only, no Kysely/DB imports).

type RequestUnsupportedReason = 'grouping' | 'unsupported-sort' | 'unsupported-filter'

export type RequestUnsupported = { reason: RequestUnsupportedReason; field?: string; operator?: string }

const VALUE_LESS_OPERATORS = new Set(['isEmpty', 'isNotEmpty'])

// Object.hasOwn, not `in`/bracket, for every field/value membership check below: the request schema accepts any
// string, so a crafted 'constructor'/'__proto__'/'toString' would resolve to an inherited Object.prototype
// member and be treated as a real, supported field or enum value.
const isEnumValueSupported = (field: FilterFieldCapability, value: unknown): boolean => {
  if (field.type !== 'scalar' || field.scalarType !== 'enum') return true
  const mapping = field.enumMapping
  if (!mapping) return true
  const values = Array.isArray(value) ? value : [value]
  return values.every((v) => typeof v === 'string' && Object.hasOwn(mapping, v))
}

// Operator support = what the executor runs (getSupportedOperators); requestFilterOperatorParity.test guards the subset.
export const isFilterItemServable = (
  item: { field: string; operator: string; value?: unknown },
  config: FilterCapabilityConfig = REQUESTS_FILTER_CAPABILITIES
): boolean => {
  const fieldConfig = Object.hasOwn(config.filterableFields, item.field) ? config.filterableFields[item.field] : undefined
  if (!fieldConfig) return false
  if (!getSupportedOperators(fieldConfig).includes(item.operator)) return false
  // Value-less operators carry no value to check against the enum mapping; the executor serves them as
  // IS [NOT] NULL regardless of it, so running the enum-value check would wrongly reject them.
  if (VALUE_LESS_OPERATORS.has(item.operator)) return true
  // Enum pill-key values absent from the backend enumMapping can't be served, so the backend 400s them.
  return isEnumValueSupported(fieldConfig, item.value)
}

// MUI won't apply a filter item until it has a value (bar the value-less operators), so a half-built item is a
// no-op, not an unsupported clause — treating it as active would disable a saved view over a clause MUI never
// applies (FE) or 400 a value the executor would itself skip (backend).
export const isIncompleteFilterItem = ({ operator, value }: { operator: string; value?: unknown }): boolean => {
  if (VALUE_LESS_OPERATORS.has(operator)) return false
  return value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)
}

// Server-side, a string value is trimmed and a blank one adds no clause (see buildRequestsKyselyFilter), so a
// whitespace-only item filters nothing there even though MUI would apply it literally in the browser.
export const isNoOpServerFilterItem = (item: { operator: string; value?: unknown }): boolean =>
  isIncompleteFilterItem(item) || (typeof item.value === 'string' && item.value.trim() === '')

export const isSortFieldServable = (field: string, config: FilterCapabilityConfig = REQUESTS_FILTER_CAPABILITIES): boolean =>
  Object.hasOwn(config.sortableFields ?? {}, field)
